// outbox-local.js — v0.171.0 — reading the queue back as if it had synced.
// ---------------------------------------------------------------------------
// Until now the outbox was write-only from the screens' point of view: a
// change went in, and the screen forgot about it. That is why a pin dropped
// offline VANISHED from the plan the moment the sheet closed — the create
// answered { queued: true } with no coordinates, nothing drew it, and a
// reload loaded the server's list which did not have it either. The work was
// safe in IndexedDB and invisible everywhere, which on site reads as "it
// didn't save".
//
// This module turns queued rows into the things a screen renders:
//
//   applyPendingToDefects(list, rows, scope)  the server's defect list with
//                                             every queued create / edit /
//                                             delete applied on top
//   reconcilePending(list, rows, scope, map)  the SCREEN's current list with
//                                             its pending marks brought back
//                                             in line with the queue (after
//                                             a sync, a discard, a new photo)
//   pendingPhotoRows(rows, defectId)          the queued photos for one pin
//   dependentRowIds(rows, localRowId)         rows that point at a queued pin
//   mergeQueuedPatch(queuedBody, patch)       edit a pin that has not been
//                                             created yet by editing its
//                                             queued create in place
//
// LOCAL IDS. A queued create has no server id, so the screen gets
// 'local:<outbox row id>' — a string a Number() would reject, which is the
// point: it can never be mistaken for a real id and sent to the API. When the
// create replays, the row id maps to the real id and the screen swaps it.
//
// Pure: rows in, plain objects out. No IndexedDB, no DOM. Tested in node.

import { ref, isRef, resolveRefs } from './outbox-refs.js';
import { classifyQueuedItem } from './outbox-identity.js';

const LOCAL_PREFIX = 'local:';

export function localIdFor(rowId) { return `${LOCAL_PREFIX}${rowId}`; }
export function isLocalId(id) { return typeof id === 'string' && id.startsWith(LOCAL_PREFIX); }
export function rowIdFromLocalId(id) {
  if (!isLocalId(id)) return null;
  const n = Number(id.slice(LOCAL_PREFIX.length));
  return Number.isFinite(n) ? n : null;
}

// '/api/defects/12?x' → { resource: 'defects', id: '12' }
function parsePath(path) {
  const segs = String(path || '').split('?')[0].split('/').filter(Boolean).filter(s => s !== 'api');
  return { resource: segs[0] || '', id: segs[1] || null };
}

// A queued photo names its pin either by real id or by a ref to a queued
// create. Either way, answer the id the SCREEN uses for that pin.
export function photoDefectKey(body) {
  const v = body && (body.defectId ?? body.defectID);
  if (isRef(v)) return localIdFor(v.__outboxRef);
  return v == null ? null : String(v);
}

function sameId(a, b) { return a != null && b != null && String(a) === String(b); }

// Only rows the current identity may replay count as "pending". A row queued
// under a different sign-in is blocked, and drawing it on this user's plan
// would be showing them someone else's work.
function replayable(rows, currentKey) {
  return (rows || []).filter(r => r && classifyQueuedItem(r, currentKey) === 'match');
}

/**
 * The defect list as it will look once the queue has synced.
 *
 * @param {Array}  list   defects as the API returned them (raw API shape)
 * @param {Array}  rows   outbox rows
 * @param {Object} scope  { projectId, currentKey }
 * @returns {Array} API-shaped rows; queued ones carry `pending: true`,
 *          created-but-unsynced ones an id of 'local:<row>'.
 */
export function applyPendingToDefects(list, rows, scope = {}) {
  const projectId = scope.projectId;
  const out = (Array.isArray(list) ? list : []).map(d => ({ ...d }));
  const pendingPhotoCounts = new Map();

  for (const row of replayable(rows, scope.currentKey)) {
    const method = String(row.method || '').toUpperCase();
    const { resource, id } = parsePath(row.path);
    const body = row.body && typeof row.body === 'object' ? row.body : {};

    if (resource === 'defects') {
      if (method === 'POST') {
        if (projectId != null && !sameId(body.projectId, projectId)) continue;
        out.push({
          ...body,
          id: localIdFor(row.id),
          defectNumber: null,
          photos: [],
          photoCount: 0,
          pending: true,
          queuedAt: row.queuedAt ?? null,
        });
      } else if (method === 'PATCH' || method === 'PUT') {
        const i = out.findIndex(d => sameId(d.id, id));
        if (i >= 0) out[i] = { ...out[i], ...body, pending: true };
      } else if (method === 'DELETE') {
        const i = out.findIndex(d => sameId(d.id, id));
        if (i >= 0) out.splice(i, 1);
      }
      continue;
    }

    if (resource === 'defect-photos' && method === 'POST') {
      const key = photoDefectKey(body);
      if (key != null) pendingPhotoCounts.set(key, (pendingPhotoCounts.get(key) || 0) + 1);
    }
  }

  for (const d of out) {
    const n = pendingPhotoCounts.get(String(d.id)) || 0;
    if (n > 0) {
      d.pendingPhotoCount = n;
      d.photoCount = (Number(d.photoCount) || 0) + n;
      d.pending = true;
    }
  }
  return out;
}

/**
 * Bring a list the screen already holds back in line with the queue.
 *
 * Unlike applyPendingToDefects this does NOT re-apply queued edits (the
 * screen applied them as they were made); it only decides, per pin, whether
 * it is still pending and how many photos are still waiting, adds any queued
 * create the screen has not seen, and drops a local pin whose queued create
 * is gone (synced — the screen swaps the id on the 'applied' event before
 * this runs — or discarded).
 *
 * @param {Array}    list    the screen's defects (any shape with id/photoCount)
 * @param {Array}    rows    outbox rows
 * @param {Object}   scope   { projectId, currentKey }
 * @param {Function} mapNew  turns an API-shaped row into the screen's shape
 */
export function reconcilePending(list, rows, scope = {}, mapNew = (x) => x) {
  const projectId = scope.projectId;
  const touched = new Set();        // real ids with a queued edit / delete / comment
  const photoCounts = new Map();    // screen id → queued photo count
  const createRows = new Map();     // String(rowId) → the queued create row

  for (const row of replayable(rows, scope.currentKey)) {
    const method = String(row.method || '').toUpperCase();
    const { resource, id } = parsePath(row.path);
    const body = row.body && typeof row.body === 'object' ? row.body : {};
    if (resource === 'defects') {
      if (method === 'POST') {
        if (projectId != null && !sameId(body.projectId, projectId)) continue;
        createRows.set(String(row.id), row);
      } else if (id != null) {
        touched.add(String(id));
      }
    } else if (resource === 'defect-photos' && method === 'POST') {
      const key = photoDefectKey(body);
      if (key != null) photoCounts.set(key, (photoCounts.get(key) || 0) + 1);
    } else if (resource === 'defect-comments' && method === 'POST') {
      const key = photoDefectKey(body);
      if (key != null) touched.add(key);
    }
  }

  const out = [];
  const seen = new Set();
  for (const d of (Array.isArray(list) ? list : [])) {
    if (!d) continue;
    const idStr = String(d.id);
    const n = photoCounts.get(idStr) || 0;
    if (isLocalId(idStr)) {
      const rowId = String(rowIdFromLocalId(idStr));
      const row = createRows.get(rowId);
      if (!row) continue;                       // synced (already swapped) or discarded
      seen.add(rowId);
      const body = row.body && typeof row.body === 'object' ? row.body : {};
      out.push({
        ...d,
        ...mapNew({ ...body, id: idStr, defectNumber: null, photos: [], pending: true, pendingPhotoCount: n, photoCount: n, queuedAt: row.queuedAt ?? null }),
        id: idStr,
      });
      continue;
    }
    const base = (Number(d.photoCount) || 0) - (Number(d.pendingPhotoCount) || 0);
    out.push({ ...d, pending: touched.has(idStr) || n > 0, pendingPhotoCount: n, photoCount: base + n });
  }
  for (const [rowId, row] of createRows) {
    if (seen.has(rowId)) continue;
    const idStr = localIdFor(rowId);
    const n = photoCounts.get(idStr) || 0;
    const body = row.body && typeof row.body === 'object' ? row.body : {};
    out.push(mapNew({ ...body, id: idStr, defectNumber: null, photos: [], pending: true, pendingPhotoCount: n, photoCount: n, queuedAt: row.queuedAt ?? null }));
  }
  return out;
}

/** Queued photo rows for one pin (real id or local id), oldest first. */
export function pendingPhotoRows(rows, defectId, currentKey) {
  if (defectId == null) return [];
  return replayable(rows, currentKey).filter(r =>
    String(r.method || '').toUpperCase() === 'POST'
    && parsePath(r.path).resource === 'defect-photos'
    && photoDefectKey(r.body) === String(defectId)
  );
}

/** The queued create row behind a local id, or null. */
export function queuedCreateRow(rows, localId) {
  const rowId = rowIdFromLocalId(localId);
  if (rowId == null) return null;
  return (rows || []).find(r => r && Number(r.id) === rowId
    && String(r.method || '').toUpperCase() === 'POST'
    && parsePath(r.path).resource === 'defects') || null;
}

/** Ids of every row whose body points at the queued row `localRowId`. */
export function dependentRowIds(rows, localRowId) {
  const out = [];
  for (const r of rows || []) {
    if (!r || Number(r.id) === Number(localRowId)) continue;
    const { unresolved } = resolveRefs(r.body, new Map());
    if (unresolved.some(u => String(u) === String(localRowId))) out.push(r.id);
  }
  return out;
}

/**
 * Editing a pin that only exists in the queue: fold the edit INTO its queued
 * create rather than queueing a PATCH against an id the server has never
 * seen. `undefined` values are ignored so a partial form cannot blank a field
 * it did not carry.
 */
export function mergeQueuedPatch(queuedBody, patch) {
  const out = { ...(queuedBody || {}) };
  for (const [k, v] of Object.entries(patch || {})) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

// Re-export for callers that build a photo against a queued pin.
export { ref as outboxRef };

/**
 * 2026-09-15 — ids (as strings) of server photos with a DELETE waiting in the
 * queue. The pin card leaves these out when it loads: a photo removed and saved
 * came back the next time the card opened, because its delete had not been sent
 * yet, and got removed — and a second delete queued — a second time. Brett:
 * "Its like i need to do it twice before it gets removed". A quarantined
 * (refused) delete does not count: that photo is still on the server.
 */
export function pendingPhotoDeleteIds(rows, currentKey) {
  const out = new Set();
  for (const r of replayable(rows, currentKey)) {
    if (r.quarantinedAt) continue;
    if (String(r.method || '').toUpperCase() !== 'DELETE') continue;
    const { resource, id } = parsePath(r.path);
    if (resource === 'defect-photos' && id != null) out.add(String(id));
  }
  return out;
}
